import { type ReactNode } from "react";
import {
  ShieldAlert,
  MapPinOff,
  Clock,
  WifiOff,
  FileQuestion,
  AlertTriangle,
  TimerReset,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui/base/button";
import { Card, CardContent } from "@/ui/base/card";
import { cn } from "@/nucleo/utilitarios";

/**
 * <ErrorState />
 *
 * Componente único para estados de erro/acesso/recurso ausente no Sistema TEG.
 * Reaproveita o design system (Card + Button + tokens semânticos) e suporta
 * tema claro/escuro automaticamente.
 *
 * Variantes cobertas:
 *  - forbidden            (403 / sem permissão)
 *  - not-found            (404 / rota inexistente)
 *  - session-expired      (token expirado)
 *  - load-failed          (erro ao carregar dados)
 *  - resource-not-found   (ID inválido / RLS bloqueou)
 *  - server-error         (500 / crash de render)
 *  - timeout              (carregamento demorando demais)
 *
 * Por padrão renderiza inline (Card). Use `fullPage` para ocupar a tela toda.
 */

export type ErrorStateVariant =
  | "forbidden"
  | "not-found"
  | "session-expired"
  | "load-failed"
  | "resource-not-found"
  | "server-error"
  | "timeout";

export interface ErrorStateAction {
  label: string;
  /** Se `to` for fornecido, navega via React Router. Senão, executa `onClick`. */
  to?: string;
  onClick?: () => void;
  variant?: "default" | "outline";
}

export interface ErrorStateProps {
  variant: ErrorStateVariant;
  title?: string;
  description?: string;
  primaryAction?: ErrorStateAction;
  secondaryAction?: ErrorStateAction;
  /** Tela cheia ocupa a viewport; inline renderiza dentro de um Card. */
  fullPage?: boolean;
  /** Detalhes técnicos opcionais (ex: mensagem do PostgREST). Renderizados em <details>. */
  technicalDetails?: string;
  className?: string;
  children?: ReactNode;
}

interface VariantPreset {
  Icon: LucideIcon;
  /** Cor do ícone: destrutivo (vermelho), aviso (amarelo) ou neutro (muted). */
  tone: "destructive" | "warning" | "muted";
  title: string;
  description: string;
}

const PRESETS: Record<ErrorStateVariant, VariantPreset> = {
  forbidden: {
    Icon: ShieldAlert,
    tone: "destructive",
    title: "Você não tem permissão para acessar esta página",
    description:
      "Seu perfil atual não permite visualizar este conteúdo. Se acredita que deveria ter acesso, fale com um administrador.",
  },
  "not-found": {
    Icon: MapPinOff,
    tone: "muted",
    title: "Página não encontrada",
    description:
      "O endereço pode estar incorreto ou a página pode ter sido removida.",
  },
  "session-expired": {
    Icon: Clock,
    tone: "warning",
    title: "Sua sessão expirou",
    description:
      "Por segurança, faça login novamente para continuar. Nada do que você fez foi perdido.",
  },
  "load-failed": {
    Icon: WifiOff,
    tone: "muted",
    title: "Não foi possível carregar esta página",
    description:
      "O sistema não conseguiu buscar as informações agora. Verifique sua conexão e tente novamente.",
  },
  "resource-not-found": {
    Icon: FileQuestion,
    tone: "muted",
    title: "Registro não encontrado",
    description:
      "O item pode ter sido removido, arquivado ou você pode não ter acesso a ele.",
  },
  "server-error": {
    Icon: AlertTriangle,
    tone: "destructive",
    title: "Algo deu errado por aqui",
    description:
      "Tivemos um erro inesperado. Nenhuma informação foi perdida. Tente recarregar; se persistir, avise um administrador.",
  },
  timeout: {
    Icon: TimerReset,
    tone: "warning",
    title: "A página está demorando mais que o normal",
    description:
      "A conexão pode estar lenta ou o sistema sobrecarregado.",
  },
};

const TONE_CLASSES: Record<VariantPreset["tone"], { bg: string; fg: string }> = {
  destructive: { bg: "bg-destructive/10", fg: "text-destructive" },
  warning: { bg: "bg-warning/10", fg: "text-warning" },
  muted: { bg: "bg-muted", fg: "text-muted-foreground" },
};

function ActionButton({ action }: { action: ErrorStateAction }) {
  const navigate = useNavigate();
  const handleClick = () => {
    if (action.onClick) {
      action.onClick();
      return;
    }
    if (action.to) {
      navigate(action.to);
    }
  };
  return (
    <Button
      onClick={handleClick}
      variant={action.variant ?? "default"}
      size="lg"
      className="w-full sm:w-auto"
    >
      {action.label}
    </Button>
  );
}

export function ErrorState({
  variant,
  title,
  description,
  primaryAction,
  secondaryAction,
  fullPage = false,
  technicalDetails,
  className,
  children,
}: ErrorStateProps) {
  const preset = PRESETS[variant];
  const { Icon, tone } = preset;
  const toneClasses = TONE_CLASSES[tone] ?? TONE_CLASSES.muted;

  const finalTitle = title ?? preset.title;
  const finalDescription = description ?? preset.description;

  const body = (
    <div className="mx-auto flex max-w-md flex-col items-center text-center gap-5 px-4 py-6">
      <div
        className={cn(
          "flex h-16 w-16 items-center justify-center rounded-full",
          toneClasses.bg,
        )}
      >
        <Icon className={cn("h-8 w-8", toneClasses.fg)} aria-hidden />
      </div>
      <div className="space-y-2">
        {/*
          `h2`, não `h1`: o estado de erro quase sempre aparece DENTRO de uma
          página que já tem seu título em h1 (`ui/PageHeader.tsx`), e dois h1
          na mesma tela dão dois níveis raiz para quem navega por cabeçalhos.
          Quando o erro ocupa a tela inteira, o h1 é o do cabeçalho que fica
          acima dele.
        */}
        <h2 className="text-xl sm:text-2xl sm:leading-8 font-semibold text-foreground leading-tight">
          {finalTitle}
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {finalDescription}
        </p>
      </div>
      {children}
      {(primaryAction || secondaryAction) && (
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center sm:w-auto">
          {primaryAction && <ActionButton action={primaryAction} />}
          {secondaryAction && (
            <ActionButton
              action={{ variant: "outline", ...secondaryAction }}
            />
          )}
        </div>
      )}
      {technicalDetails && (
        <details className="w-full text-left text-xs text-muted-foreground bg-muted/40 rounded-md p-3">
          <summary className="cursor-pointer select-none">
            Detalhes técnicos
          </summary>
          <pre className="mt-2 whitespace-pre-wrap wrap-break-word">
            {technicalDetails}
          </pre>
        </details>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div
        className={cn(
          "flex min-h-dvh items-center justify-center bg-muted/40 px-4 py-6",
          className,
        )}
      >
        {body}
      </div>
    );
  }

  return (
    <Card className={cn("w-full", className)}>
      <CardContent className="py-8">{body}</CardContent>
    </Card>
  );
}

export default ErrorState;
